import type { CuttingStrategy } from './CuttingStrategy';
import type { IMaterial } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';
import { MaxRectsStrategy } from './MaxRectsStrategy';

export interface IOrientationCompareResult extends ICutResult {
    portraitWaste: number;
    landscapeWaste: number;
    bestOrientation: 'portrait' | 'landscape';
    portraitMaterials: IMaterial[];
    landscapeMaterials: IMaterial[];
    comparisonImage?: string; // Base64 data URL for comparison visualization
}

export class OrientationCompareStrategy implements CuttingStrategy {
    name = 'Orientation Compare';
    private baseStrategy = new MaxRectsStrategy(); // Using MaxRects as base strategy for optimal packing

    optimize(material: IMaterial, parts: IMaterial[]): IOrientationCompareResult {
        // Run optimization with original orientation (portrait)
        const portraitResult = this.baseStrategy.optimize(material, parts);

        // Create a rotated material (landscape)
        const landscapeMaterial: IMaterial = {
            ...material,
            width: material.height,
            height: material.width
        };

        // Rotate all parts for landscape comparison
        const rotatedParts = parts.map(part => ({
            ...part,
            width: part.height,
            height: part.width
        }));

        // Run optimization with landscape orientation
        const landscapeResult = this.baseStrategy.optimize(landscapeMaterial, rotatedParts);

        // Transform landscape results back to original orientation for proper visualization
        const transformedLandscapeMaterials = landscapeResult.usedMaterials.map(mat => {
            const rotatedMat: IMaterial = {
                ...mat,
                width: material.width, // Original width
                height: material.height, // Original height
                children: mat.children?.map(child => ({
                    ...child,
                    // Swap x/y coordinates and adjust
                    x: child.y,
                    y: child.x,
                    // Swap width/height back
                    width: child.height,
                    height: child.width
                }))
            };
            return rotatedMat;
        });

        // Determine best orientation based on waste percentage
        const bestOrientation =
            portraitResult.wastePercentage <= landscapeResult.wastePercentage ? 'portrait' : 'landscape';

        // Use the best result for the final output
        const finalResult =
            bestOrientation === 'portrait' ? portraitResult : landscapeResult;

        const finalMaterials =
            bestOrientation === 'portrait' ? portraitResult.usedMaterials : transformedLandscapeMaterials;

        return {
            usedMaterials: finalMaterials,
            wastePercentage: finalResult.wastePercentage,
            strategyName: this.name,
            portraitWaste: portraitResult.wastePercentage,
            landscapeWaste: landscapeResult.wastePercentage,
            bestOrientation,
            portraitMaterials: portraitResult.usedMaterials,
            landscapeMaterials: transformedLandscapeMaterials
        };
    }
}
